/**
 * Terminal — die Ansicht, die im Alltag benutzt wird.
 *
 * Bewusst nur ein Handgriff: Wer hereinkommt, sieht seinen Zustand und genau
 * die Schaltflächen, die daraus folgen. Alles Übrige (Woche, Export, Profil)
 * liegt hinter Reitern, damit der häufigste Vorgang der schnellste bleibt.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  breakMs, checkDay, dailyTargetMs, dayKey, de, fmtClock, fmtDuration, fmtSigned,
  hhmm, longDateLabel, netMs, overtimeBalanceMs, terminalStatus, weekStart, weekSummary,
  byDate, type ArbzgWarning, type DayEntry, type StampAction,
} from '@zeit/domain';
import { serverNow } from '../lib/clock.ts';
import { ApiError, api, read } from '../lib/appwrite.ts';
import { useNow } from '../lib/session.ts';
import type { Session } from '../lib/session.ts';
import { Card, Figure, Kicker, Loading, Notices, Page, Stat, StatusBanner } from '../ui/index.tsx';

const t = de.terminal;

const HINTS = {
  off: t.hintOff,
  on: t.hintOn,
  break: t.hintBreak,
  done: t.hintDone,
} as const;

export function Terminal({
  session,
  onToast,
}: {
  session: Session;
  onToast: (text: string, tone?: 'error') => void;
}) {
  const { employee } = session;
  const employeeId = employee.$id!;

  const [entries, setEntries] = useState<DayEntry[] | null>(null);
  const [previousOut, setPreviousOut] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const today = dayKey(serverNow());
  const monday = weekStart(today);

  const load = useCallback(async () => {
    // Die laufende Woche reicht für alles auf dieser Seite ausser dem Saldo;
    // der Saldo holt sich seinen eigenen, längeren Zeitraum.
    const [week, history, prev] = await Promise.all([
      read.days(employeeId, monday, today),
      read.days(employeeId, employee.employmentStart, today),
      read.previousClockOut(employeeId, today),
    ]);
    const merged = byDate([...history, ...week]);
    setEntries([...merged.values()]);
    setPreviousOut(prev);
  }, [employeeId, monday, today, employee.employmentStart]);

  useEffect(() => {
    void load().catch(() => onToast(de.error.generic, 'error'));
  }, [load, onToast]);

  const entry = entries?.find((e) => e.date === today) ?? null;
  const status = terminalStatus(entry);
  const running = status === 'on' || status === 'break';
  const now = useNow(running);

  const stamp = async (action: StampAction) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.stamp(action);
      await load();
      onToast(de.toast[action]);
    } catch (err) {
      const e = err as ApiError;
      onToast(e.message || de.error.generic, 'error');
      // Bei einem Konflikt hat ein anderes Gerät gestempelt — neu laden,
      // damit der Bildschirm nicht weiter etwas Falsches anbietet.
      if (e.code === 'conflict') await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const warnings: ArbzgWarning[] = useMemo(
    () => (entry ? checkDay(entry, now, previousOut) : []),
    [entry, now, previousOut],
  );

  if (!entries) return <Loading />;

  const workMs = netMs(entry, now);
  const pauseMs = entry ? breakMs(entry, now) : 0;

  const week = weekSummary(employee, monday, byDate(entries), now);
  const balance = overtimeBalanceMs(employee, entries, now, today);
  const dayTarget = dailyTargetMs(employee.weeklyTargetHours, employee.workdaysPerWeek);
  const remaining = week.targetMs - week.netMs;

  return (
    <Page title={de.nav.today} kicker={longDateLabel(today)}>
      <div className="grid-2">
        <Card title={t.stampTitle} flush>
          <StatusBanner
            status={status}
            label={entry?.status === 'missing_out' ? de.status.missingOut : de.status[status]}
          />

          <div className="stats stats-flush">
            <Stat label={t.workToday} value={<span className="num">{fmtClock(workMs)}</span>} />
            <Stat label={t.breakToday} value={<span className="num">{fmtClock(pauseMs)}</span>} muted />
          </div>

          <div className="stack" style={{ padding: 'var(--space-4)' }}>
            <StampButtons status={status} busy={busy} onStamp={stamp} workMs={workMs} />

            <Notices warnings={warnings} />

            <div className="text-muted" style={{ fontSize: 12 }}>
              {entry?.status === 'missing_out' ? t.hintMissingOut : HINTS[status]}
              {status === 'on' && workMs < dayTarget && (
                <> {t.remaining(fmtDuration(dayTarget - workMs))} bis zum Tagessoll.</>
              )}
            </div>
          </div>
        </Card>

        <div className="stack">
          <Card title={t.logTitle} aside={employee.name} flush>
            <StampLog entry={entry} />
          </Card>

          <Card title={t.weekVsTarget}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <Figure label={t.workWeek} value={fmtDuration(week.netMs)} size="lg" />
              <span style={{ fontSize: 13, color: 'var(--color-neutral-700)' }}>
                {t.ofTarget(fmtDuration(week.targetMs))}
              </span>
            </div>

            <div>
              <div
                className="bar"
                role="progressbar"
                aria-valuenow={Math.round(week.ratio * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`bar-fill${week.ratio > 1 ? ' bar-fill-over' : ''}`}
                  style={{ width: `${Math.min(100, week.ratio * 100)}%` }}
                />
              </div>

              <div
                className="text-muted"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 8,
                  fontSize: 12,
                }}
              >
                <span>{t.pctOfTarget(`${Math.round(week.ratio * 100)} %`)}</span>
                <span>
                  {remaining > 0
                    ? t.remaining(fmtDuration(remaining))
                    : t.surplus(fmtDuration(-remaining))}
                </span>
              </div>
            </div>

            <div className="stats">
              <Stat label={t.weekDiff} value={<span className="num">{fmtSigned(week.diffMs)}</span>} small />
              <Stat label={t.balance} value={<span className="num">{fmtSigned(balance)}</span>} small />
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}

/**
 * Die Schaltflächen des jeweiligen Zustands.
 *
 * Es wird nur angeboten, was die Zustandsmaschine erlaubt — ein „Gehen“, das
 * ohne „Kommen“ sichtbar wäre, könnte man anklicken und bekäme nur eine
 * Fehlermeldung.
 */
function StampButtons({
  status,
  busy,
  onStamp,
  workMs,
}: {
  status: ReturnType<typeof terminalStatus>;
  busy: boolean;
  onStamp: (a: StampAction) => void;
  workMs: number;
}) {
  if (status === 'off') {
    return (
      <button type="button" className="btn btn-stamp" disabled={busy} onClick={() => onStamp('clock_in')}>
        {t.clockIn}
      </button>
    );
  }

  if (status === 'break') {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <button
          type="button"
          className="btn btn-stamp btn-stamp-break"
          disabled={busy}
          onClick={() => onStamp('break_end')}
        >
          {t.breakEnd}
        </button>
        <button
          type="button"
          className="btn btn-stamp btn-stamp-sm btn-stamp-outline"
          disabled={busy}
          onClick={() => onStamp('clock_out')}
        >
          {t.clockOut}
        </button>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
        <div
          style={{
            minHeight: 104,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '0 22px',
            border: 'var(--border)',
          }}
        >
          <Kicker>{t.netToday}</Kicker>
          <span
            className="num"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 'clamp(22px,3vw,30px)',
            }}
          >
            {fmtDuration(workMs)}
          </span>
        </div>
        <button
          type="button"
          className="btn btn-stamp btn-stamp-sm btn-stamp-outline"
          style={{ fontSize: 20 }}
          disabled={busy}
          onClick={() => onStamp('clock_in')}
        >
          {t.clockInAgain}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
      <button
        type="button"
        className="btn btn-stamp btn-stamp-sm btn-stamp-outline"
        disabled={busy}
        onClick={() => onStamp('break_start')}
      >
        {t.breakStart}
      </button>
      <button
        type="button"
        className="btn btn-stamp btn-stamp-sm"
        disabled={busy}
        onClick={() => onStamp('clock_out')}
      >
        {t.clockOut}
      </button>
    </div>
  );
}

function StampLog({ entry }: { entry: DayEntry | null }) {
  if (!entry?.clockIn) {
    return (
      <p className="text-muted" style={{ fontSize: 13, margin: 'var(--space-4)' }}>
        {t.logEmpty}
      </p>
    );
  }

  const multiple = entry.breaks.length > 1;
  const rows: { label: string; time: string }[] = [{ label: t.logClockIn, time: hhmm(entry.clockIn) }];

  entry.breaks.forEach((b, i) => {
    const n = multiple ? ` ${i + 1}` : '';
    rows.push({ label: `${t.logBreakStart}${n}`, time: hhmm(b.s) });
    rows.push({ label: `${t.logBreakEnd}${n}`, time: b.e ? hhmm(b.e) : t.running });
  });

  rows.push({ label: t.logClockOut, time: entry.clockOut ? hhmm(entry.clockOut) : t.pending });

  return (
    <div>
      {rows.map((r, i) => (
        <div
          key={`${r.label}-${i}`}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 16,
            padding: '11px var(--space-4)',
            borderBottom: '1px solid var(--color-divider)',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500 }}>{r.label}</span>
          <span
            className="num"
            style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 17 }}
          >
            {r.time}
          </span>
        </div>
      ))}
    </div>
  );
}
